import { FEEDBACK_METRIC_IDS, FEEDBACK_SOURCES, type FeedbackMetricId, type FeedbackSource } from "@domains/feedback/shared/feedbackTypes.ts";
import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { DEFAULT_REPORTING_SCOPE, REPORTING_SCOPES, type ReportingScope } from "@packages/core/organization/scope.ts";
import { createParser, parseAsStringEnum, parseAsStringLiteral, useQueryState, useQueryStates } from "nuqs";
import { useCallback, useMemo } from "react";
import type { FeedbackReportFilters, FeedbackReportSentiment } from "#shared/feedbackReportFilters.ts";
import type { FeedbackReportInput } from "#shared/feedbackReportContract.ts";
import { CUSTOM_FEEDBACK_PERIOD, DEFAULT_FEEDBACK_TIME_FRAME, FEEDBACK_TIME_FRAMES, type FeedbackPeriod, getFeedbackDataFloor, resolveFeedbackPeriod, toFeedbackPeriodInput } from "./feedbackPeriod.ts";
import { toFeedbackReportType } from "./toFeedbackReportType.ts";

export const FEEDBACK_SCOPE_QUERY_KEY = "feedback-scope";
const FEEDBACK_PERIOD_QUERY_KEY = "feedback-period";
const FEEDBACK_START_QUERY_KEY = "feedback-start";
const FEEDBACK_END_QUERY_KEY = "feedback-end";
export const FEEDBACK_TYPE_QUERY_KEY = "feedback-type";
export const FEEDBACK_SENTIMENT_QUERY_KEY = "feedback-sentiment";
export const FEEDBACK_METRIC_QUERY_KEY = "feedback-metric";

export const FEEDBACK_PERIOD_QUERY_KEYS = [FEEDBACK_PERIOD_QUERY_KEY, FEEDBACK_START_QUERY_KEY, FEEDBACK_END_QUERY_KEY] as const;
export const FEEDBACK_FILTER_QUERY_KEYS = [FEEDBACK_TYPE_QUERY_KEY, FEEDBACK_SENTIMENT_QUERY_KEY, FEEDBACK_METRIC_QUERY_KEY] as const;

export const FEEDBACK_FILTER_SENTIMENTS = ["Positive", "Constructive"] as const satisfies ReadonlyArray<FeedbackReportSentiment>;
export type FeedbackFilterSentiment = (typeof FEEDBACK_FILTER_SENTIMENTS)[number];

const QUERY_STATE_OPTIONS = { history: "replace" as const, clearOnDefault: true, shallow: false };

const scopeParser = parseAsStringEnum<ReportingScope>([...REPORTING_SCOPES])
    .withDefault(DEFAULT_REPORTING_SCOPE)
    .withOptions(QUERY_STATE_OPTIONS);

const parseAsCalendarDate = createParser({
    parse: value => {
        try {
            return parseDate(value);
        } catch {
            return null;
        }
    },
    serialize: value => value.toString(),
    eq: (a, b) => a.compare(b) === 0
});

const feedbackPeriodParsers = {
    period: parseAsStringLiteral([...FEEDBACK_TIME_FRAMES, CUSTOM_FEEDBACK_PERIOD]).withDefault(DEFAULT_FEEDBACK_TIME_FRAME),
    start: parseAsCalendarDate,
    end: parseAsCalendarDate
};

const FEEDBACK_PERIOD_URL_KEYS = {
    period: FEEDBACK_PERIOD_QUERY_KEY,
    start: FEEDBACK_START_QUERY_KEY,
    end: FEEDBACK_END_QUERY_KEY
};

const feedbackFilterParsers = {
    feedbackType: parseAsStringEnum<FeedbackSource>([...FEEDBACK_SOURCES]),
    sentiment: parseAsStringEnum<FeedbackFilterSentiment>([...FEEDBACK_FILTER_SENTIMENTS]),
    metricId: parseAsStringEnum<FeedbackMetricId>([...FEEDBACK_METRIC_IDS])
};

const FEEDBACK_FILTER_URL_KEYS = {
    feedbackType: FEEDBACK_TYPE_QUERY_KEY,
    sentiment: FEEDBACK_SENTIMENT_QUERY_KEY,
    metricId: FEEDBACK_METRIC_QUERY_KEY
};

export function useFeedbackScope() {
    return useQueryState(FEEDBACK_SCOPE_QUERY_KEY, scopeParser);
}

export function useFeedbackPeriod(scope: ReportingScope) {
    const [{ period, start, end }, setParams] = useQueryStates(feedbackPeriodParsers, { ...QUERY_STATE_OPTIONS, urlKeys: FEEDBACK_PERIOD_URL_KEYS });

    const resolved = useMemo(() => resolveFeedbackPeriod({ period, start, end }, { floor: getFeedbackDataFloor(scope), today: today(getLocalTimeZone()) }), [period, start, end, scope]);

    const setPeriod = useCallback((next: FeedbackPeriod) => void setParams(next.kind === "preset" ? { period: next.timeFrame, start: null, end: null } : { period: CUSTOM_FEEDBACK_PERIOD, start: next.start, end: next.end }), [setParams]);

    return [resolved, setPeriod] as const;
}

export function useFeedbackFilterState() {
    return useQueryStates(feedbackFilterParsers, { ...QUERY_STATE_OPTIONS, urlKeys: FEEDBACK_FILTER_URL_KEYS });
}

/** @public */
export function useFeedbackReportFilters(): FeedbackReportFilters {
    const [{ feedbackType, sentiment, metricId }] = useFeedbackFilterState();

    return useMemo(() => ({ type: feedbackType ? toFeedbackReportType(feedbackType) : null, sentiment, metricId }), [feedbackType, sentiment, metricId]);
}

export interface FeedbackReportSelection {
    scope: ReportingScope;
    period: FeedbackPeriod;
    filters: FeedbackReportFilters;
    input: FeedbackReportInput;
}

export function useFeedbackReportSelection(): FeedbackReportSelection {
    const [scope] = useFeedbackScope();
    const [period] = useFeedbackPeriod(scope);
    const filters = useFeedbackReportFilters();

    return useMemo(
        () => ({
            scope,
            period,
            filters,
            input: {
                scope,
                ...toFeedbackPeriodInput(period),
                type: filters.type ?? undefined,
                sentiment: filters.sentiment ?? undefined,
                metricId: filters.metricId ?? undefined
            }
        }),
        [scope, period, filters]
    );
}
